import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { AddButtonAutocomplete } from '@/__core/hocs';
import { LabeledField } from '@/__core/components/LabeledField';
import { getItemUomBackendOptions } from '../../redux/ItemUomCRUD';

interface UomOption {
  id: string;
  name: string;
}

export interface ItemUomAddModalSavePayload {
  name: string;
  conversion?: {
    to_uom_id: string;
    ratio: number;
  };
}

interface ItemUomAddModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: ItemUomAddModalSavePayload) => Promise<any>;
  loading?: boolean;
  uomOptions?: UomOption[];
  defaultToUomId?: string;
}

export default function ItemUomAddModal({
  open,
  onClose,
  onSave,
  loading = false,
  defaultToUomId,
}: ItemUomAddModalProps) {
  const [name, setName] = useState('');
  const [fetchedUomOptions, setFetchedUomOptions] = useState<UomOption[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [shouldDeclareConversion, setShouldDeclareConversion] = useState(false);
  const [toUomId, setToUomId] = useState('');
  const [ratio, setRatio] = useState('1');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const busy = loading || submitting;

  // Fetch UOM list from backend when modal opens
  useEffect(() => {
    if (!open) return;
    setOptionsLoading(true);
    getItemUomBackendOptions()
      .then((options) => {
        setFetchedUomOptions(options);
      })
      .catch(() => {
        setFetchedUomOptions([]);
      })
      .finally(() => {
        setOptionsLoading(false);
      });
  }, [open]);

  const suggestedToUomId = useMemo(
    () => defaultToUomId || fetchedUomOptions[0]?.id || '',
    [defaultToUomId, fetchedUomOptions],
  );

  useEffect(() => {
    if (!open) {
      return;
    }

    setError(null);
    setToUomId((current) => {
      if (!fetchedUomOptions.length) {
        return '';
      }

      if (current && fetchedUomOptions.some((option) => option.id === current)) {
        return current;
      }

      return suggestedToUomId;
    });
  }, [open, suggestedToUomId, fetchedUomOptions]);

  const resetState = useCallback(() => {
    setName('');
    setShouldDeclareConversion(false);
    setToUomId(suggestedToUomId);
    setRatio('1');
    setError(null);
  }, [suggestedToUomId]);

  const handleClose = useCallback(() => {
    if (busy) return;
    resetState();
    onClose();
  }, [busy, onClose, resetState]);

  const validate = () => {
    const trimmed = name.trim();
    if (!trimmed) {
      return 'Tên ĐVT là bắt buộc.';
    }

    if (shouldDeclareConversion && fetchedUomOptions.length > 0) {
      if (!toUomId) {
        return 'ĐVT đích là bắt buộc khi khai báo quy đổi.';
      }

      const parsedRatio = Number(ratio);
      if (!Number.isFinite(parsedRatio) || parsedRatio <= 0) {
        return 'Tỷ lệ quy đổi phải lớn hơn 0.';
      }
    }

    return null;
  };

  const handleSave = useCallback(async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const payload: ItemUomAddModalSavePayload = {
        name: name.trim(),
      };

      if (shouldDeclareConversion && fetchedUomOptions.length > 0 && toUomId) {
        payload.conversion = {
          to_uom_id: toUomId,
          ratio: Number(ratio),
        };
      }

      await onSave(payload);
      resetState();
      onClose();
    } catch (saveError: any) {
      setError(saveError?.message || 'Có lỗi xảy ra khi tạo ĐVT');
    } finally {
      setSubmitting(false);
    }
  }, [name, onClose, onSave, ratio, resetState, shouldDeclareConversion, toUomId, fetchedUomOptions.length]);

  const handleKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      void handleSave();
    }
  }, [handleSave]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='sm'
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: '1px solid #e5e7eb',
          pb: 2,
          fontSize: '1.25rem',
          fontWeight: 600,
          color: '#111827',
        }}
      >
        Thêm ĐVT và quy đổi
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        <Box sx={{ color: '#6b7280', fontSize: '0.875rem', my: 2 }}>
          <LabeledField label='Tên ĐVT' error={!!error && !name.trim()} helperText={!!error && !name.trim() ? error : undefined} labelWidth={120}>
            <TextField
              fullWidth
              placeholder='Nhập tên ĐVT'
              value={name}
              onChange={(event) => {
                setName(event.target.value);
                if (error) {
                  setError(null);
                }
              }}
              onKeyDown={handleKeyDown}
              error={!!error && !name.trim()}
              disabled={busy}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 1,
                },
              }}
            />
          </LabeledField>

          {optionsLoading ? (
            <Stack direction='row' spacing={1} alignItems='center' sx={{ my: 1, pl: '122px' }}>
              <CircularProgress size={14} />
              <Typography variant='caption' sx={{ color: '#6b7280' }}>Đang tải danh sách ĐVT...</Typography>
            </Stack>
          ) : fetchedUomOptions.length > 0 ? (
            <>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={shouldDeclareConversion}
                    onChange={(event) => {
                      setShouldDeclareConversion(event.target.checked);
                      if (error) {
                        setError(null);
                      }
                    }}
                    disabled={busy}
                    size='small'
                  />
                }
                label='Quy đổi ĐVT'
                sx={{ my: 1, ml: 0 }}
              />

              {shouldDeclareConversion && (
                <>
                  <LabeledField label='ĐVT đích' labelWidth={120}>
                    <AddButtonAutocomplete
                      options={fetchedUomOptions}
                      value={fetchedUomOptions.find((opt) => opt.id === toUomId) || null}
                      onChange={(_event, newValue) => {
                        const option = newValue as UomOption | null;
                        setToUomId(option?.id || '');
                        if (error) {
                          setError(null);
                        }
                      }}
                      placeholder='Chọn ĐVT đích'
                      noOptionsText='Không có ĐVT nào'
                      size='small'
                      fullWidth
                      disabled={busy}
                    />
                  </LabeledField>

                  <LabeledField label='Tỷ lệ' labelWidth={120}>
                    <TextField
                      type='number'
                      size='small'
                      value={ratio}
                      onChange={(event) => {
                        setRatio(event.target.value);
                        if (error) {
                          setError(null);
                        }
                      }}
                      onKeyDown={handleKeyDown}
                      error={!!error && (!Number.isFinite(Number(ratio)) || Number(ratio) <= 0)}
                      disabled={busy}
                      inputProps={{ min: 0.000001, step: 0.000001 }}
                      fullWidth
                    />
                  </LabeledField>

                  <Typography variant='caption' sx={{ color: '#6b7280', pl: '122px' }}>
                    Công thức: Số lượng ĐVT đích = Số lượng ĐVT mới x Tỷ lệ.
                  </Typography>
                </>
              )}
            </>
          ) : (
            <Typography variant='caption' sx={{ color: '#6b7280' }}>
              Chưa có ĐVT nào để khai báo quy đổi. Bạn vẫn có thể tạo ĐVT mới.
            </Typography>
          )}

          {error && name.trim() ? (
            <Typography variant='caption' sx={{ color: '#ef4444' }}>{error}</Typography>
          ) : null}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          borderTop: '1px solid #e5e7eb',
          p: 2,
          gap: 2,
        }}
      >
        <Button
          onClick={handleClose}
          variant='outlined'
          disabled={busy}
          sx={{
            borderColor: '#d1d5db',
            color: '#6b7280',
            '&:hover': {
              borderColor: '#9ca3af',
              backgroundColor: '#f9fafb',
            },
          }}
        >
          Hủy
        </Button>
        <Button
          onClick={() => {
            void handleSave();
          }}
          variant='contained'
          disabled={!name.trim() || busy}
          startIcon={busy ? <CircularProgress size={16} /> : null}
          sx={{
            backgroundColor: '#10b981',
            '&:hover': {
              backgroundColor: '#059669',
            },
            '&:disabled': {
              backgroundColor: '#d1d5db',
              color: '#9ca3af',
            },
          }}
        >
          {busy ? 'Đang tạo...' : 'Tạo mới'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
